/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetSearchPhrase } from "~/hooks/useGetSearchPhrase";
import { DEFAULT_PAGE_SIZE, LOAD_MORE_INCREMENT } from "./consts";
import type { EventResourceController, LoadRequest, Params } from "./types";
import { deriveViewConfig, deriveViewState } from "./utils";

/**
 * Manages the complex presentation logic for the event resources list.
 *
 * This hook acts as a controller that takes raw data (resource groups, loading states)
 * and transforms it into a structured state ready for rendering. It handles the logic
 * for searching, pagination (load more/less), and determining the overall view state
 * (e.g., showing a loader, a hint message, or the content).
 *
 * It returns a comprehensive object containing:
 * - The derived view state and UI flags (e.g., `viewState`, `showLoader`).
 * - The formatted data to be rendered (`groups`).
 * - Callbacks to handle user actions (`loadMore`, `clearSearch`).
 * - State related to search and pagination.
 *
 * @param params - Data, loading states, and state update functions from the parent component.
 * @returns An `EventResourceController` object with all state and callbacks needed by the UI.
 */
export function useEventResourceController({
  resourceStateGroups,
  resourceSearchState,
  isLoadingResourceStates,
  updateResourceSearchState,
  refetchResourceStates,
  removeResourceSearchState,
  hasResources,
}: Params): EventResourceController {
  const { phrase: validatedPhrase, isSearchAllowed } = useGetSearchPhrase(
    resourceSearchState.searchPhrase
  );

  const hasSearchPhrase = (resourceSearchState.searchPhrase?.length ?? 0) > 0;
  const isSearchPhraseToShort = hasSearchPhrase && !isSearchAllowed;
  const isSearchActive = !!validatedPhrase;

  const [loadRequest, setLoadRequest] = useState<LoadRequest>(null);
  const [settledSearchPhrase, setSettledSearchPhrase] = useState<
    string | undefined
  >(() => validatedPhrase);

  const groups = useMemo(
    () => resourceStateGroups ?? [],
    [resourceStateGroups]
  );
  const hasGroups = groups.length > 0;
  const hasFetchedResources = resourceStateGroups !== undefined;

  const currentResourceCount = useMemo(
    () =>
      groups.reduce(
        (acc, group) => acc + (group.resourceStates?.length ?? 0),
        0
      ),
    [groups]
  );

  const canLoadLess = currentResourceCount > DEFAULT_PAGE_SIZE;

  const handleLoad = useCallback(
    async (loadMore: boolean) => {
      const currentLimit = Number(
        resourceSearchState.limit ?? DEFAULT_PAGE_SIZE
      );
      const nextLimit = loadMore
        ? currentLimit + LOAD_MORE_INCREMENT
        : DEFAULT_PAGE_SIZE;

      setLoadRequest(loadMore ? "more" : "less");

      const payload = {
        ...resourceSearchState,
        page: undefined,
        limit: nextLimit.toString(),
      };

      updateResourceSearchState(payload);
      await refetchResourceStates(nextLimit);
    },
    [refetchResourceStates, resourceSearchState, updateResourceSearchState]
  );

  const loadMore = useCallback(() => handleLoad(true), [handleLoad]);
  const loadLess = useCallback(() => handleLoad(false), [handleLoad]);

  useEffect(() => {
    if (!isLoadingResourceStates) {
      setLoadRequest(null);
      setSettledSearchPhrase(validatedPhrase);
    }
  }, [isLoadingResourceStates, validatedPhrase]);

  const clearSearch = useCallback<EventResourceController["clearSearch"]>(
    (mode) => {
      if (!removeResourceSearchState) {
        return;
      }

      const shouldClear =
        hasSearchPhrase ||
        (resourceSearchState.page !== undefined &&
          Number(resourceSearchState.page) !== 1);

      if (!shouldClear) {
        return;
      }

      removeResourceSearchState(["searchPhrase", "page"], mode);
    },
    [hasSearchPhrase, removeResourceSearchState, resourceSearchState.page]
  );

  const isSearchPending =
    isSearchActive && settledSearchPhrase !== validatedPhrase;

  const isRefetching =
    isLoadingResourceStates && loadRequest === null && hasFetchedResources;

  const viewStateContext = useMemo(
    () => ({
      hasFetchedResources,
      hasSearchPhrase,
      hasGroups,
      hasResources,
      isSearchPhraseToShort,
      isLoadingResourceStates,
      isSearchActive,
      isSearchPending,
      isRefetching,
      loadRequest,
      settledSearchPhrase,
    }),
    [
      hasFetchedResources,
      hasSearchPhrase,
      hasGroups,
      hasResources,
      isSearchPhraseToShort,
      isLoadingResourceStates,
      isSearchActive,
      isSearchPending,
      isRefetching,
      loadRequest,
      settledSearchPhrase,
    ]
  );

  const viewState = useMemo(
    () => deriveViewState(viewStateContext),
    [viewStateContext]
  );

  const { showHeader, showLoader, showLoadMore } = useMemo(
    () => deriveViewConfig(viewState, viewStateContext),
    [viewState, viewStateContext]
  );

  return {
    groups,
    currentResourceCount,
    canLoadLess,
    viewState,
    showHeader,
    showLoader,
    showLoadMore,
    loadRequest,
    loadMore,
    loadLess,
    clearSearch,
    resourceSearchState,
    updateResourceSearchState,
    removeResourceSearchState,
    isLoadingResourceStates,
  };
}
